import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  // Base styles - accessible, beautiful defaults
  [
    'inline-flex items-center justify-center gap-2',
    'font-medium transition-all duration-200 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'select-none whitespace-nowrap',
  ],
  {
    variants: {
      variant: {
        // Primary - Bold action button with accent color
        primary: [
          'bg-accent-600 text-white',
          'hover:bg-accent-700 active:bg-accent-800',
          'focus-visible:ring-accent-500',
          'shadow-soft hover:shadow-soft-md',
        ],
        // Secondary - Subtle alternative
        secondary: [
          'bg-primary-900 text-white',
          'hover:bg-primary-800 active:bg-primary-700',
          'focus-visible:ring-primary-500',
          'shadow-soft hover:shadow-soft-md',
        ],
        // Outline - Border only
        outline: [
          'border-2 border-surface-300 bg-transparent text-surface-700',
          'hover:border-accent-500 hover:text-accent-600 hover:bg-accent-50',
          'focus-visible:ring-accent-500',
        ],
        // Ghost - Minimal styling
        ghost: [
          'bg-transparent text-surface-600',
          'hover:bg-surface-100 hover:text-surface-900',
          'focus-visible:ring-surface-400',
        ],
        // Danger - Destructive actions
        danger: [
          'bg-danger-600 text-white',
          'hover:bg-danger-700 active:bg-danger-800',
          'focus-visible:ring-danger-500',
          'shadow-soft hover:shadow-soft-md',
        ],
        // Success - Positive actions
        success: [
          'bg-success-600 text-white',
          'hover:bg-success-700 active:bg-success-800',
          'focus-visible:ring-success-500',
          'shadow-soft hover:shadow-soft-md',
        ],
        // Link - Text button
        link: [
          'bg-transparent text-accent-600 underline-offset-4',
          'hover:underline hover:text-accent-700',
          'focus-visible:ring-accent-500',
          'p-0 h-auto',
        ],
      },
      size: {
        sm: 'h-8 px-3 text-sm rounded-lg',
        md: 'h-10 px-4 text-sm rounded-xl',
        lg: 'h-12 px-6 text-base rounded-xl',
        xl: 'h-14 px-8 text-lg rounded-2xl',
        icon: 'h-10 w-10 rounded-xl',
        'icon-sm': 'h-8 w-8 rounded-lg',
        'icon-lg': 'h-12 w-12 rounded-xl',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, loading, disabled, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg
            className="h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button, buttonVariants }
